import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useNavigate as useRouterNavigate,
  useLocation as useRouterLocation,
  NavigateOptions,
} from "react-router-dom";

interface FlowState {
  step: string;
  state: any;
}

interface PersistedFlowState extends FlowState {
  /**
   * The router location.key this progress was captured on. location.state
   * survives a reload unchanged (same history entry, same key) — it's only
   * fresh when the key differs, i.e. a genuinely new navigation happened.
   */
  locationKey: string;
}

interface OnboardingContextValue {
  currentStep: string;
  currentState: any;
  goTo: (step: string, options?: NavigateOptions) => void;
  goBack: () => void;
}

const OnboardingCtx = createContext<OnboardingContextValue | null>(null);

export const useOnboardingContext = () => useContext(OnboardingCtx);

/**
 * Reads a step machine's last-seen position back out of sessionStorage, so a
 * reload mid-flow can resume at the right step instead of restarting.
 */
export const getPersistedFlowState = (persistKey: string): PersistedFlowState | null => {
  try {
    const raw = sessionStorage.getItem(persistKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearPersistedFlowState = (persistKey: string) => {
  try {
    sessionStorage.removeItem(persistKey);
  } catch {
    // ignore
  }
};

interface OnboardingProviderProps {
  initialStep: string;
  initialState?: any;
  steps: Set<string>;
  onExitStep: (step: string, options?: NavigateOptions) => void;
  /** When set, the current step + state is mirrored to sessionStorage on every change. */
  persistKey?: string;
  children: ReactNode;
}

export const OnboardingProvider = ({
  initialStep,
  initialState = null,
  steps,
  onExitStep,
  persistKey,
  children,
}: OnboardingProviderProps) => {
  const routerNavigate = useRouterNavigate();
  const routerLocation = useRouterLocation();
  const [current, setCurrent] = useState<FlowState>({ step: initialStep, state: initialState });
  const historyRef = useRef<FlowState[]>([]);

  useEffect(() => {
    if (!persistKey) return;

    try {
      const persisted: PersistedFlowState = { ...current, locationKey: routerLocation.key };
      sessionStorage.setItem(persistKey, JSON.stringify(persisted));
    } catch {
      // ignore
    }
  }, [persistKey, current, routerLocation.key]);

  const goTo = useCallback(
    (step: string, options?: NavigateOptions) => {
      if (!steps.has(step)) {
        onExitStep(step, options);
        return;
      }

      setCurrent((prev) => {
        if (!options?.replace) {
          historyRef.current = [...historyRef.current, prev];
        }
        return { step, state: options?.state ?? null };
      });
    },
    [steps, onExitStep],
  );

  const goBack = useCallback(() => {
    const stack = historyRef.current;
    const prev = stack[stack.length - 1];

    if (prev) {
      historyRef.current = stack.slice(0, -1);
      setCurrent(prev);
    } else {
      routerNavigate(-1);
    }
  }, [routerNavigate]);

  const value = useMemo(
    () => ({ currentStep: current.step, currentState: current.state, goTo, goBack }),
    [current, goTo, goBack],
  );

  return <OnboardingCtx.Provider value={value}>{children}</OnboardingCtx.Provider>;
};

export const useFlowNavigate = () => {
  const ctx = useContext(OnboardingCtx);
  const routerNavigate = useRouterNavigate();

  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      if (!ctx) {
        routerNavigate(to as any, options);
        return;
      }

      if (typeof to === "number") {
        if (to < 0) {
          ctx.goBack();
        } else {
          routerNavigate(to);
        }
        return;
      }

      ctx.goTo(to, options);
    },
    [ctx, routerNavigate],
  );
};

export const useFlowLocation = () => {
  const ctx = useContext(OnboardingCtx);
  const routerLocation = useRouterLocation();

  if (!ctx) return routerLocation;

  return {
    pathname: ctx.currentStep,
    state: ctx.currentState,
    search: "",
    hash: "",
    key: "onboarding",
  };
};

export const FlowRedirect = ({ to, replace = false }: { to: string; replace?: boolean }) => {
  const navigate = useFlowNavigate();

  useEffect(() => {
    navigate(to, { replace });
  }, []);

  return null;
};
